import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import './Dashboard.css';
import { useAuth } from '../../AuthContext';
import { useQuery } from '@tanstack/react-query';
import { fetchEvents } from '../../services/resourceService';

const monthNames = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"
];
const dayNames = [
  "SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"
];

function DashboardCalendar({ orgId, className = 'dashboard-card admin-calendar-card', maxVisibleEvents = null }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeTab, setActiveTab] = useState('all');
  const { token } = useAuth();
  const navigate = useNavigate();

  // Define date range for query
  // Fixed start date: after 2025-10-29T00:00:00Z
  const queryStartDate = '2025-10-29T00:00:00Z';
  const queryEndDate = new Date(new Date(queryStartDate).setFullYear(new Date(queryStartDate).getFullYear() + 1)).toISOString().slice(0, 19) + 'Z';

  // React Query: Fetch Events
  const { data, isLoading: loading, error } = useQuery({
    queryKey: ['events', orgId, queryStartDate, queryEndDate],
    queryFn: () => fetchEvents({ orgId, startDate: queryStartDate, endDate: queryEndDate }),
    enabled: !!orgId && !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
  });

  const events = useMemo(() => {
    if (!data?.items) return [];

    return data.items.flatMap((event, index) => {
      const rawType = (event.eventType || '').toUpperCase();

      let eventTypeDisplay = 'events'; 
      if (rawType === 'HOLIDAY') {
        eventTypeDisplay = 'holidays';
      } else if (rawType.includes('EXAM') || rawType === 'EXAM_TERM') {
        eventTypeDisplay = 'exams';
      } else if (rawType === 'MEETING') {
        eventTypeDisplay = 'meetings';
      }

      const subEvents = Object.values(event.eventScheduleMap || {}).flat();

      return subEvents.map((subEvent, subIndex) => {
        const eventDateTimeStr = subEvent.eventDateAndTime;
        const eventDateObj = eventDateTimeStr ? new Date(eventDateTimeStr) : null;

        return {
          eventId: event.eventId || `event-${index}-${subIndex}`,
          eventName: subEvent.subEventName || event.eventName || `Event ${index + 1}`,
          eventType: eventTypeDisplay,
          eventDescription: event.eventDescription || 'No description available',
          date: eventDateObj,
          eventDate: eventDateObj
            ? eventDateObj.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : 'Date not available',
          location: subEvent.location || 'N/A',
          duration: subEvent.durationMins || 'N/A',
          className: index % 2 === 0 ? 'purple' : 'green',
        };
      });
    });
  }, [data]);

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    let current = new Date(startDate);

    while (days.length < 42) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const filteredEvents = activeTab === 'all'
    ? events
    : events.filter(event => event.eventType === activeTab);

  const visibleEvents = maxVisibleEvents
    ? filteredEvents.slice(0, maxVisibleEvents)
    : filteredEvents;

  const handleEventClick = (event) => {
    navigate('/events/details', { state: { event } });
  };

  return (
    <div className={className}>
      <div className="card-header">
        <h3 className="card-title">Calendar</h3>
      </div>

      <div className="calendar-tabs">
        {["all", "holidays", "events", "exams", "meetings"].map(tab => (
          <button
            key={tab}
            className={`calendar-tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)}>←</button>
        <span className="calendar-month-year">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button className="calendar-nav-btn" onClick={() => navigateMonth(1)}>→</button>
      </div>

      <div className="calendar-grid">
        {dayNames.map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        {calendarDays.map((day, idx) => {
          const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
          const isToday = day.toDateString() === new Date().toDateString();
          const isSelected = day.toDateString() === selectedDate.toDateString();

          const hasEvent = filteredEvents.some(
            event => event.date && event.date.toDateString() === day.toDateString()
          );

          let dayClassName = "calendar-day";
          if (!isCurrentMonth) dayClassName += " other-month";
          if (isToday) dayClassName += " today";
          if (isSelected && !isToday) dayClassName += " selected";
          if (hasEvent && isCurrentMonth) dayClassName += " event";

          return (
            <button
              key={idx}
              onClick={() => setSelectedDate(day)}
              className={dayClassName}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <div className="events-list">
        {loading ? (
          <div className="calendar-empty-state">Loading events...</div>
        ) : error ? (
          <div className="calendar-empty-state">Error loading events</div>
        ) : filteredEvents.length === 0 ? (
          <div className="calendar-empty-state">No {activeTab === 'all' ? 'events' : activeTab} found.</div>
        ) : (
          visibleEvents.map((event, idx) => (
            <div
              key={idx}
              className={`event-item ${event.className}`}
              onClick={() => handleEventClick(event)}
            >
              <div className="event-content">
                <div>
                  <h4 className="event-title" style={{ color: '#fff7ed', fontWeight: 700, marginBottom: '6px' }}>
                    {event.eventName}
                  </h4>
                  <p className="event-date" style={{ color: '#fef3c7', fontWeight: 600, fontSize: '0.875rem', opacity: 1 }}>
                    {event.eventDate}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DashboardCalendar;
